import { useState, useEffect } from 'react'

/**
 * Debounce a value you already have.
 *
 * The sibling `useDebounceSearch` does three things at once: it OWNS the input state, debounces
 * it, and hands back `[input, setInput, debounced]`. That is right for a search box a component
 * renders itself, and wrong for everything else — five places in this app were hand-rolling a
 * `useEffect` + `setTimeout` + `clearTimeout` because the value they needed to debounce came
 * from a prop, from a sibling's state, or from a derived expression, and there was nothing to
 * reach for. This is that missing half.
 *
 * Deliberately just the debounce. If the timer needs to do something other than set state —
 * write a URL param, reset a page number, guard against clobbering restored history — that
 * belongs at the call site, not behind another option on this hook. Two of the five callers are
 * exactly that case and were left alone:
 *
 *  - `ObligationsTab` writes the debounced term into a URL param, not into state.
 *  - `RulesProfilesTab` additionally guards a `page=N` restored by Back from being wiped by a
 *    timer that fires after the navigation — a real bug it fixed, with the reasoning recorded
 *    in place.
 *
 * @param value  the value to debounce
 * @param delay  milliseconds to wait after the last change (default 300)
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debounced
}
